import { supabase } from '@/lib/supabase';

// ── Modèle de "blueprint" : description déclarative d'une app complète ──
// Ce format est produit par l'IA (edge function) OU rédigé à la main
// (génération depuis le chat). buildAppFromBlueprint le transforme en
// lignes dans les tables du builder.

export interface BlueprintBlock {
  type: string; // heading | text | image | list | form | table | spacer
  content: Record<string, unknown>;
}

export interface BlueprintPage {
  title: string;
  slug: string;
  icon?: string;
  visibility?: string;
  blocks: BlueprintBlock[];
}

export interface BlueprintField {
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
}

export interface BlueprintForm {
  name: string;
  title?: string;
  description?: string;
  fields: BlueprintField[];
}

export interface BlueprintTableField {
  label: string;
  type: string;
}

export interface BlueprintTable {
  name: string;
  slug: string;
  fields: BlueprintTableField[];
  rows?: Record<string, string>[]; // chaque clé = libellé de champ
}

export interface BlueprintSetting {
  key: string;
  label: string;
  type: string; // text | number | toggle | select
  value?: string;
  options?: string[];
}

export interface AppBlueprint {
  name?: string;
  pages: BlueprintPage[];
  forms?: BlueprintForm[];
  tables?: BlueprintTable[];
  settings?: BlueprintSetting[];
}

export interface BuildResult {
  pages: number;
  blocks: number;
  forms: number;
  formFields: number;
  tables: number;
  tableFields: number;
  tableRows: number;
  settings: number;
}

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'page';

// ── Suppression du contenu existant d'une app (dans l'ordre des dépendances) ──

async function deleteInWhere(table: string, column: string, values: number[]) {
  if (values.length === 0) return;
  await supabase.from(table).delete().in(column, values);
}

export async function clearAppContent(appId: number): Promise<void> {
  const pageIds = (await supabase.from('app_pages').select('id').eq('app_id', appId)).data?.map((r) => r.id) || [];
  const formIds = (await supabase.from('app_forms').select('id').eq('app_id', appId)).data?.map((r) => r.id) || [];
  const tableIds = (await supabase.from('app_tables').select('id').eq('app_id', appId)).data?.map((r) => r.id) || [];

  await deleteInWhere('app_blocks', 'page_id', pageIds);
  await deleteInWhere('app_pages', 'id', pageIds);

  await deleteInWhere('app_form_fields', 'form_id', formIds);
  await deleteInWhere('app_form_responses', 'form_id', formIds);
  await deleteInWhere('app_forms', 'id', formIds);

  await deleteInWhere('app_table_fields', 'table_id', tableIds);
  await deleteInWhere('app_table_rows', 'table_id', tableIds);
  await deleteInWhere('app_tables', 'id', tableIds);

  await supabase.from('app_settings').delete().eq('app_id', appId);
}

// ── Construction d'une app complète à partir d'un blueprint ──

export async function buildAppFromBlueprint(appId: number, bp: AppBlueprint): Promise<BuildResult> {
  const result: BuildResult = {
    pages: 0,
    blocks: 0,
    forms: 0,
    formFields: 0,
    tables: 0,
    tableFields: 0,
    tableRows: 0,
    settings: 0,
  };

  // 1. Formulaires + champs
  const formIdByName = new Map<string, number>();
  for (const [i, form] of (bp.forms || []).entries()) {
    const { data, error } = await supabase
      .from('app_forms')
      .insert({
        app_id: appId,
        name: form.name,
        title: form.title || form.name,
        description: form.description || null,
        position: i,
      })
      .select('id')
      .single();
    if (error) throw error;
    formIdByName.set(form.name, data.id);
    result.forms += 1;

    for (const [fi, field] of (form.fields || []).entries()) {
      const hasOptions = ['select', 'radio', 'checkbox'].includes(field.type);
      const { error: fErr } = await supabase.from('app_form_fields').insert({
        form_id: data.id,
        label: field.label,
        type: field.type,
        required: !!field.required,
        options: hasOptions ? field.options || [] : [],
        position: fi,
      });
      if (fErr) throw fErr;
      result.formFields += 1;
    }
  }

  // 2. Tables + champs + lignes
  const tableIdBySlug = new Map<string, number>();
  for (const [i, table] of (bp.tables || []).entries()) {
    const { data, error } = await supabase
      .from('app_tables')
      .insert({ app_id: appId, name: table.name, slug: table.slug, position: i })
      .select('id')
      .single();
    if (error) throw error;
    tableIdBySlug.set(table.slug, data.id);
    result.tables += 1;

    const fieldIdByLabel = new Map<string, number>();
    for (const [fi, field] of (table.fields || []).entries()) {
      const { data: fd, error: fErr } = await supabase
        .from('app_table_fields')
        .insert({ table_id: data.id, label: field.label, type: field.type, position: fi })
        .select('id')
        .single();
      if (fErr) throw fErr;
      fieldIdByLabel.set(field.label, fd.id);
      result.tableFields += 1;
    }

    for (const row of table.rows || []) {
      const rowData: Record<string, unknown> = {};
      for (const [label, value] of Object.entries(row)) {
        const fid = fieldIdByLabel.get(label);
        if (fid !== undefined) rowData[String(fid)] = value;
      }
      const { error: rErr } = await supabase.from('app_table_rows').insert({
        table_id: data.id,
        data: rowData,
      });
      if (rErr) throw rErr;
      result.tableRows += 1;
    }
  }

  // 3. Réglages
  for (const [i, s] of (bp.settings || []).entries()) {
    const { error } = await supabase.from('app_settings').insert({
      app_id: appId,
      key: s.key,
      label: s.label,
      type: s.type,
      value: s.value ?? (s.type === 'toggle' ? '0' : ''),
      options: s.type === 'select' ? s.options || [] : [],
      position: i,
    });
    if (error) throw error;
    result.settings += 1;
  }

  // 4. Pages + blocs
  for (const [i, page] of (bp.pages || []).entries()) {
    const { data, error } = await supabase
      .from('app_pages')
      .insert({
        app_id: appId,
        title: page.title,
        slug: page.slug || slugify(page.title),
        icon: page.icon || 'ri-file-text-line',
        position: i,
        visibility: page.visibility || 'visible',
      })
      .select('id')
      .single();
    if (error) throw error;
    result.pages += 1;

    for (const [bi, block] of (page.blocks || []).entries()) {
      let content: Record<string, unknown> = { ...(block.content || {}) };
      if (block.type === 'form') {
        const name = block.content?.form as string;
        content = { formId: formIdByName.get(name) ?? null };
      } else if (block.type === 'table') {
        const slug = block.content?.table as string;
        content = { tableId: tableIdBySlug.get(slug) ?? null };
      }
      const { error: bErr } = await supabase.from('app_blocks').insert({
        page_id: data.id,
        type: block.type,
        content,
        position: bi,
      });
      if (bErr) throw bErr;
      result.blocks += 1;
    }
  }

  return result;
}

// ── Fusion incrémentale (chat IA) ──────────────────────────────
// Contrairement à buildAppFromBlueprint, cette fonction NE supprime rien :
// elle complète l'app existante (pages manquantes, blocs ajoutés en fin de
// page, formulaires / tables / réglages absents).

export interface MergeResult extends BuildResult {
  createdPages: string[];
  updatedPages: string[];
}

export async function mergeAppFromBlueprint(appId: number, bp: AppBlueprint): Promise<MergeResult> {
  const result: MergeResult = {
    pages: 0,
    blocks: 0,
    forms: 0,
    formFields: 0,
    tables: 0,
    tableFields: 0,
    tableRows: 0,
    settings: 0,
    createdPages: [],
    updatedPages: [],
  };

  const { data: existingForms } = await supabase.from('app_forms').select('id, name').eq('app_id', appId);
  const { data: existingTables } = await supabase.from('app_tables').select('id, slug').eq('app_id', appId);
  const { data: existingSettings } = await supabase.from('app_settings').select('key').eq('app_id', appId);
  const { data: existingPages } = await supabase
    .from('app_pages')
    .select('id, slug, title, position')
    .eq('app_id', appId)
    .order('position', { ascending: true });

  const formIdByName = new Map<string, number>();
  (existingForms || []).forEach((f) => { formIdByName.set(String(f.name), Number(f.id)); });
  const tableIdBySlug = new Map<string, number>();
  (existingTables || []).forEach((t) => { tableIdBySlug.set(String(t.slug), Number(t.id)); });
  const settingKeys = new Set<string>();
  (existingSettings || []).forEach((s) => { settingKeys.add(String(s.key)); });
  const pagesBySlug = new Map<string, { id: number; position: number }>();
  (existingPages || []).forEach((p) => {
    pagesBySlug.set(String(p.slug), { id: Number(p.id), position: Number(p.position) || 0 });
  });
  const nextPosition = (existingPages || []).reduce((max, p) => Math.max(max, ((p.position as number) || 0) + 1), 0);
  let pagePosition = nextPosition;

  for (const [i, form] of (bp.forms || []).entries()) {
    if (formIdByName.has(form.name)) continue;
    const { data, error } = await supabase
      .from('app_forms')
      .insert({
        app_id: appId,
        name: form.name,
        title: form.title || form.name,
        description: form.description || null,
        position: i,
      })
      .select('id')
      .single();
    if (error) throw error;
    formIdByName.set(form.name, data.id);
    result.forms += 1;

    for (const [fi, field] of (form.fields || []).entries()) {
      const hasOptions = ['select', 'radio', 'checkbox'].includes(field.type);
      const { error: fieldError } = await supabase.from('app_form_fields').insert({
        form_id: data.id,
        label: field.label,
        type: field.type,
        required: !!field.required,
        options: hasOptions ? field.options || [] : [],
        position: fi,
      });
      if (fieldError) throw fieldError;
      result.formFields += 1;
    }
  }

  for (const [i, table] of (bp.tables || []).entries()) {
    if (tableIdBySlug.has(table.slug)) continue;
    const { data, error } = await supabase
      .from('app_tables')
      .insert({ app_id: appId, name: table.name, slug: table.slug, position: i })
      .select('id')
      .single();
    if (error) throw error;
    tableIdBySlug.set(table.slug, data.id);
    result.tables += 1;

    const fieldIdByLabel = new Map<string, number>();
    for (const [fi, field] of (table.fields || []).entries()) {
      const { data: fieldRow, error: fieldError } = await supabase
        .from('app_table_fields')
        .insert({ table_id: data.id, label: field.label, type: field.type, position: fi })
        .select('id')
        .single();
      if (fieldError) throw fieldError;
      fieldIdByLabel.set(field.label, fieldRow.id);
      result.tableFields += 1;
    }

    for (const row of table.rows || []) {
      const rowData: Record<string, unknown> = {};
      Object.entries(row).forEach(([label, value]) => {
        const fieldId = fieldIdByLabel.get(label);
        if (fieldId !== undefined) rowData[String(fieldId)] = value;
      });
      const { error: rowError } = await supabase.from('app_table_rows').insert({ table_id: data.id, data: rowData });
      if (rowError) throw rowError;
      result.tableRows += 1;
    }
  }

  for (const [i, setting] of (bp.settings || []).entries()) {
    if (settingKeys.has(setting.key)) continue;
    const { error } = await supabase.from('app_settings').insert({
      app_id: appId,
      key: setting.key,
      label: setting.label,
      type: setting.type,
      value: setting.value ?? (setting.type === 'toggle' ? '0' : ''),
      options: setting.type === 'select' ? setting.options || [] : [],
      position: i,
    });
    if (error) throw error;
    settingKeys.add(setting.key);
    result.settings += 1;
  }

  for (const page of bp.pages || []) {
    const slug = page.slug || slugify(page.title);
    const existingPage = pagesBySlug.get(slug);
    let pageId: number;

    if (existingPage) {
      pageId = existingPage.id;
      result.updatedPages.push(page.title);
    } else {
      const { data, error } = await supabase
        .from('app_pages')
        .insert({
          app_id: appId,
          title: page.title,
          slug,
          icon: page.icon || 'ri-file-text-line',
          position: pagePosition,
          visibility: page.visibility || 'visible',
        })
        .select('id')
        .single();
      if (error) throw error;
      pageId = data.id;
      pagePosition += 1;
      pagesBySlug.set(slug, { id: pageId, position: pagePosition });
      result.pages += 1;
      result.createdPages.push(page.title);
    }

    const { data: lastBlock } = await supabase
      .from('app_blocks')
      .select('position')
      .eq('page_id', pageId)
      .order('position', { ascending: false })
      .limit(1);
    let blockPosition = lastBlock && lastBlock.length > 0 ? Number(lastBlock[0].position) + 1 : 0;

    for (const block of page.blocks || []) {
      let content: Record<string, unknown> = { ...(block.content || {}) };
      if (block.type === 'form') {
        const name = block.content?.form as string;
        content = { formId: formIdByName.get(name) ?? null };
      } else if (block.type === 'table') {
        const tableSlug = block.content?.table as string;
        content = { tableId: tableIdBySlug.get(tableSlug) ?? null };
      }
      const { error: blockError } = await supabase.from('app_blocks').insert({
        page_id: pageId,
        type: block.type,
        content,
        position: blockPosition,
      });
      if (blockError) throw blockError;
      blockPosition += 1;
      result.blocks += 1;
    }
  }

  return result;
}

export async function deletePageBySlug(appId: number, slug: string): Promise<boolean> {
  const { data } = await supabase
    .from('app_pages')
    .select('id')
    .eq('app_id', appId)
    .eq('slug', slug)
    .maybeSingle();
  if (!data) return false;
  await supabase.from('app_blocks').delete().eq('page_id', data.id);
  const { error } = await supabase.from('app_pages').delete().eq('id', data.id);
  return !error;
}

// ── Snapshot complet de l'app (pour l'audit et le provisionnement) ──

export interface AppSnapshot {
  pages: { title: string; slug: string; icon: string; blocks: { type: string; content: Record<string, unknown> }[] }[];
  forms: { name: string; title: string; fields: { label: string; type: string; required: boolean; options: string[] }[] }[];
  tables: { name: string; slug: string; fields: { label: string; type: string }[]; rows: Record<string, unknown>[] }[];
  settings: { key: string; label: string; type: string }[];
}

export async function loadAppSnapshot(appId: number): Promise<AppSnapshot> {
  const { data: pages } = await supabase
    .from('app_pages')
    .select('id, title, slug, icon, position')
    .eq('app_id', appId)
    .order('position', { ascending: true });
  const pageList: any[] = pages || [];
  const pageIds = pageList.map((p) => Number(p.id));
  let blockList: any[] = [];
  if (pageIds.length > 0) {
    const { data: blocks } = await supabase
      .from('app_blocks')
      .select('page_id, type, content, position')
      .in('page_id', pageIds)
      .order('position', { ascending: true });
    blockList = blocks || [];
  }

  const { data: forms } = await supabase.from('app_forms').select('id, name, title').eq('app_id', appId);
  const { data: formFields } = await supabase.from('app_form_fields').select('form_id, label, type, required, options');
  const { data: tables } = await supabase.from('app_tables').select('id, name, slug').eq('app_id', appId);
  const { data: tableFields } = await supabase.from('app_table_fields').select('table_id, label, type');
  const { data: rows } = await supabase.from('app_table_rows').select('table_id, data');
  const { data: settings } = await supabase.from('app_settings').select('key, label, type').eq('app_id', appId);

  return {
    pages: pageList.map((page) => ({
      title: String(page.title),
      slug: String(page.slug),
      icon: String(page.icon || 'ri-file-text-line'),
      blocks: blockList
        .filter((block) => Number(block.page_id) === Number(page.id))
        .map((block) => ({ type: String(block.type), content: (block.content || {}) as Record<string, unknown> })),
    })),
    forms: (forms || []).map((form) => ({
      name: String(form.name),
      title: String(form.title || form.name),
      fields: (formFields || [])
        .filter((field) => Number(field.form_id) === Number(form.id))
        .map((field) => ({
          label: String(field.label),
          type: String(field.type || 'text'),
          required: !!field.required,
          options: (field.options as string[]) || [],
        })),
    })),
    tables: (tables || []).map((table) => ({
      name: String(table.name),
      slug: String(table.slug),
      fields: (tableFields || [])
        .filter((field) => Number(field.table_id) === Number(table.id))
        .map((field) => ({ label: String(field.label), type: String(field.type || 'text') })),
      rows: (rows || [])
        .filter((row) => Number(row.table_id) === Number(table.id))
        .map((row) => (row.data || {}) as Record<string, unknown>),
    })),
    settings: (settings || []).map((setting) => ({
      key: String(setting.key),
      label: String(setting.label),
      type: String(setting.type || 'text'),
    })),
  };
}